/**
 * pass-only-restriction.js
 * ---------------------------------------------------------------------------
 * Enforces: products tagged "pass-only" (configurable) cannot be in the same
 * cart as any other product. Works both directions:
 *   - Cart already has a pass-only item  -> block adding anything else
 *   - Cart already has other items       -> SWAP: clear the cart, then add
 *                                            the pass-only item
 *
 * WHY THIS APPROACH
 * /cart.js line items do not include product tags, so we can't check tags at
 * add-time without extra API calls. Instead, Liquid bakes a list of exclusive
 * product/variant IDs into the page on load (see pass-only-config.liquid).
 * That list is checked both against what's already in the cart and against
 * whatever is about to be added.
 *
 * SWAP BEHAVIOR (NEW)
 * If the cart already contains other (non pass-only) products and the user
 * adds a pass-only product, we no longer block the add. Instead we silently
 * clear the entire cart via /cart/clear.js and then let the pass-only add
 * proceed, so the cart ends up containing only the pass-only item.
 *
 * The reverse direction is still blocked, not swapped: if a pass-only item
 * is already in the cart and the user tries to add something else, we show
 * the restriction message and stop the add. We don't auto-remove the
 * pass-only item in that direction, since silently discarding it in favor
 * of an unrelated product would be a much more surprising/destructive
 * action for the user to have happen without confirmation.
 *
 * WHAT THIS COVERS
 *   - Standard product-form "Add to cart" submissions (fetch or XHR/jQuery)
 *   - Quick-add buttons on collection/search pages that POST to /cart/add.js
 *   - Cart drawer / cart page quantity changes via /cart/change.js, /cart/update.js
 *   - A defensive check on the cart page itself that disables the Checkout
 *     button if an invalid mix is ever detected (belt-and-suspenders)
 *
 * KNOWN LIMITATION
 *   Dynamic checkout buttons ("Buy it now", Shop Pay, PayPal, G Pay) are
 *   rendered by Shopify inside a sandboxed element that this script cannot
 *   intercept the same way it intercepts /cart/add.js. Instead, this script
 *   hides those buttons on the product page when adding would create an
 *   invalid mix (see guardDynamicCheckoutButtons). This is a strong deterrent
 *   but, unlike the /cart/add.js interception, is not a hard server-side
 *   guarantee -- combine with the checkout-app option if you need that.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // CONFIG
  // Populated by Liquid (see pass-only-config.liquid) as window.__exclusiveConfig.
  // Falls back to an empty/manual config if that snippet isn't installed yet,
  // so this file never throws even if config is missing.
  // ---------------------------------------------------------------------
  var CONFIG = window.__exclusiveConfig || {
    tag: 'pass-only',
    productIds: [],   // e.g. [7891234567890]
    variantIds: []    // e.g. [41234567890123]
  };

  var MESSAGE = 'This product must be purchased separately. Remove it before adding another product.';
  var SWAP_MESSAGE = 'Your cart was cleared because this item must be purchased separately.';

  var CART_ADD_PATTERNS = ['/cart/add.js', '/cart/add'];
  var CART_WRITE_PATTERNS = CART_ADD_PATTERNS.concat(['/cart/update.js', '/cart/update', '/cart/change.js', '/cart/change']);

  var CHECKOUT_SELECTORS = [
    '#checkout',
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href="/checkout"]',
    '.cart__checkout-button',
    '[data-checkout-button]'
  ];

  var DYNAMIC_CHECKOUT_SELECTORS = [
    '.shopify-payment-button',
    '[data-shopify="payment-button"]'
  ];

  // ---------------------------------------------------------------------
  // ID HELPERS
  // ---------------------------------------------------------------------
  function toNum(v) {
    var n = Number(v);
    return isNaN(n) ? null : n;
  }

  function isExclusiveProductId(id) {
    var n = toNum(id);
    return n !== null && CONFIG.productIds.indexOf(n) !== -1;
  }

  function isExclusiveVariantId(id) {
    var n = toNum(id);
    return n !== null && CONFIG.variantIds.indexOf(n) !== -1;
  }

  function lineItemIsExclusive(item) {
    return isExclusiveVariantId(item.variant_id) || isExclusiveProductId(item.product_id);
  }

  // ---------------------------------------------------------------------
  // CART STATE
  // ---------------------------------------------------------------------
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function cartState(cart) {
    var hasExclusive = false;
    var hasOther = false;
    (cart.items || []).forEach(function (item) {
      if (lineItemIsExclusive(item)) hasExclusive = true;
      else hasOther = true;
    });
    return { hasExclusive: hasExclusive, hasOther: hasOther };
  }

  // incomingFlags: array of booleans, one per item being added, true = exclusive
  function additionAllowed(state, incomingFlags) {
    var addingExclusive = incomingFlags.indexOf(true) !== -1;
    var addingOther = incomingFlags.indexOf(false) !== -1;

    if (addingExclusive && addingOther) return false;      // mixed batch in one request
    if (state.hasExclusive && addingOther) return false;   // exclusive item already in cart
    // state.hasOther && addingExclusive is intentionally NOT blocked here --
    // that case triggers a cart swap instead (see shouldSwapCart), handled
    // upstream before additionAllowed is consulted.
    return true;
  }

  // True specifically for the "swap" case: cart has other (non pass-only)
  // items already, and the incoming batch is a pure pass-only add.
  function shouldSwapCart(state, incomingFlags) {
    var addingExclusive = incomingFlags.indexOf(true) !== -1;
    var addingOther = incomingFlags.indexOf(false) !== -1;
    return state.hasOther && !state.hasExclusive && addingExclusive && !addingOther;
  }

  // ---------------------------------------------------------------------
  // Clears the entire cart via /cart/clear.js. Used right before adding a
  // pass-only product to a cart that already has other items -- rather than
  // blocking the add, we empty the cart and let the pass-only add proceed.
  // ---------------------------------------------------------------------
  function clearCart() {
    return originalFetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  // ---------------------------------------------------------------------
  // REQUEST BODY PARSING
  // Handles the shapes themes commonly use to call /cart/add.js:
  //   - JSON string: {"id":123,"quantity":1} or {"items":[{"id":1,"quantity":1}, ...]}
  //   - FormData (native product-form submissions)
  //   - URL-encoded string ("id=123&quantity=1")
  // ---------------------------------------------------------------------
  function extractIncomingVariantIds(body) {
    var ids = [];
    try {
      if (typeof body === 'string') {
        var trimmed = body.trim();
        if (trimmed.charAt(0) === '{') {
          var parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed.items)) {
            parsed.items.forEach(function (it) {
              if (it && it.id) ids.push(it.id);
            });
          } else if (parsed.id) {
            ids.push(parsed.id);
          }
        } else {
          var params = new URLSearchParams(trimmed);
          if (params.get('id')) ids.push(params.get('id'));
          // Some themes submit items[][id]=... for multi-add forms
          params.forEach(function (value, key) {
            if (/^items\[\d+\]\[id\]$/.test(key)) ids.push(value);
          });
        }
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var idVal = body.get('id');
        if (idVal) ids.push(idVal);
      }
    } catch (e) {
      // Fail open: if we can't parse it, don't block a request we don't understand.
      return [];
    }
    return ids;
  }

  function isCartWriteUrl(url) {
    return CART_WRITE_PATTERNS.some(function (p) { return url.indexOf(p) !== -1; });
  }

  function isCartAddUrl(url) {
    return CART_ADD_PATTERNS.some(function (p) { return url.indexOf(p) !== -1; });
  }

  // ---------------------------------------------------------------------
  // UI: toast message + persistent cart-page banner
  // ---------------------------------------------------------------------
  function showMessage(text) {
    var el = document.getElementById('pass-only-restriction-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'pass-only-restriction-toast';
      el.setAttribute('role', 'alert');
      el.style.cssText = [
        'position:fixed', 'left:50%', 'bottom:24px', 'transform:translateX(-50%)',
        'background:#1a1a1a', 'color:#fff', 'padding:12px 20px', 'border-radius:6px',
        'font-size:14px', 'line-height:1.4', 'z-index:99999', 'max-width:90vw',
        'text-align:center', 'box-shadow:0 4px 14px rgba(0,0,0,.25)',
        'transition:opacity .2s ease', 'opacity:0'
      ].join(';');
      document.body.appendChild(el);
    }
    el.textContent = text;
    requestAnimationFrame(function () { el.style.opacity = '1'; });
    clearTimeout(showMessage._t);
    showMessage._t = setTimeout(function () { el.style.opacity = '0'; }, 4500);
  }

  function showBanner() {
    var el = document.getElementById('pass-only-restriction-banner');
    if (!el) {
      el = document.createElement('div');
      el.id = 'pass-only-restriction-banner';
      el.setAttribute('role', 'alert');
      el.style.cssText = [
        'background:#fdecea', 'color:#611a15', 'padding:12px 16px', 'font-size:14px',
        'text-align:center', 'border:1px solid #f5c6cb', 'border-radius:4px', 'margin:12px 0'
      ].join(';');
      el.textContent = MESSAGE;
      var mount = document.querySelector('[data-cart-items], .cart, #main-cart-items, main') || document.body;
      mount.insertBefore(el, mount.firstChild);
    }
  }

  function hideBanner() {
    var el = document.getElementById('pass-only-restriction-banner');
    if (el) el.remove();
  }

  function setCheckoutDisabled(disabled) {
    CHECKOUT_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (btn) {
        if (disabled) {
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-disabled', 'true');
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.5';
        } else {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
        }
      });
    });
  }

  // ---------------------------------------------------------------------
  // GUARD: defensive re-check of whatever is currently in the cart.
  // Runs on load, on an interval, and whenever guardCartPage() is called
  // manually (e.g. after your cart drawer refreshes).
  // ---------------------------------------------------------------------
  function guardCartPage() {
    getCart().then(function (cart) {
      var state = cartState(cart);
      var invalid = state.hasExclusive && state.hasOther;
      setCheckoutDisabled(invalid);
      if (invalid) showBanner(); else hideBanner();
    }).catch(function () { /* ignore transient network errors */ });
  }

  // ---------------------------------------------------------------------
  // GUARD: hide dynamic checkout buttons (Buy it now / Shop Pay / etc.)
  // on the current product page when adding it would break the rule.
  // Best-effort visual guard -- see file header for why this can't be a
  // hard block the way /cart/add.js interception is.
  // ---------------------------------------------------------------------
  function currentProductFormVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input ? input.value : null;
  }

  function guardDynamicCheckoutButtons() {
    var variantId = currentProductFormVariantId();
    if (!variantId) return;

    getCart().then(function (cart) {
      var state = cartState(cart);
      var addingExclusive = isExclusiveVariantId(variantId);
      // Only the "exclusive item already in cart, adding other" direction
      // still needs a hard block on dynamic checkout buttons; the "adding
      // exclusive to a cart with other items" direction is now a swap, not
      // a block, so it never needs to hide the buttons.
      var blocked = !addingExclusive && state.hasExclusive;

      DYNAMIC_CHECKOUT_SELECTORS.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (btn) {
          btn.style.display = blocked ? 'none' : '';
        });
      });
    }).catch(function () { /* ignore */ });
  }

  // ---------------------------------------------------------------------
  // INTERCEPT: fetch
  // ---------------------------------------------------------------------
  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    init = init || {};

    if (!isCartWriteUrl(url) || !init.body) {
      return originalFetch.apply(window, arguments);
    }

    var isAdd = isCartAddUrl(url);
    var args = arguments;

    var proceed = function () {
      var result = originalFetch.apply(window, args);
      result.then(function () { guardCartPage(); guardDynamicCheckoutButtons(); }).catch(function () {});
      return result;
    };

    if (!isAdd) {
      // /cart/update.js and /cart/change.js only change quantities of items
      // already in the cart, they can't introduce a new product -- let them
      // through, then re-verify state afterward.
      return proceed();
    }

    var incomingIds = extractIncomingVariantIds(init.body);
    if (incomingIds.length === 0) return proceed();

    return getCart().then(function (cart) {
      var state = cartState(cart);
      var incomingFlags = incomingIds.map(isExclusiveVariantId);

      if (shouldSwapCart(state, incomingFlags)) {
        return clearCart().then(function () {
          showMessage(SWAP_MESSAGE);
          return proceed();
        });
      }

      if (!additionAllowed(state, incomingFlags)) {
        showMessage(MESSAGE);
        return Promise.reject(new Error('pass-only-restriction: blocked'));
      }
      return proceed();
    });
  };

  // ---------------------------------------------------------------------
  // INTERCEPT: XMLHttpRequest (covers jQuery.ajax-based themes)
  // ---------------------------------------------------------------------
  var originalOpen = XMLHttpRequest.prototype.open;
  var originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__passOnlyUrl = url || '';
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    var xhr = this;
    var url = this.__passOnlyUrl || '';

    if (!isCartWriteUrl(url) || !body) {
      return originalSend.apply(this, arguments);
    }

    if (!isCartAddUrl(url)) {
      var res = originalSend.apply(this, arguments);
      xhr.addEventListener('loadend', function () { guardCartPage(); guardDynamicCheckoutButtons(); });
      return res;
    }

    var incomingIds = extractIncomingVariantIds(body);
    if (incomingIds.length === 0) {
      return originalSend.apply(this, arguments);
    }

    getCart().then(function (cart) {
      var state = cartState(cart);
      var incomingFlags = incomingIds.map(isExclusiveVariantId);

      if (shouldSwapCart(state, incomingFlags)) {
        return clearCart().then(function () {
          showMessage(SWAP_MESSAGE);
          originalSend.call(xhr, body);
          xhr.addEventListener('loadend', function () { guardCartPage(); guardDynamicCheckoutButtons(); });
        });
      }

      if (!additionAllowed(state, incomingFlags)) {
        showMessage(MESSAGE);
        // Emulate a failed request so calling code's error handlers run
        // instead of silently hanging.
        Object.defineProperty(xhr, 'status', { value: 422, configurable: true });
        Object.defineProperty(xhr, 'readyState', { value: 4, configurable: true });
        if (typeof xhr.onreadystatechange === 'function') xhr.onreadystatechange();
        if (typeof xhr.onerror === 'function') xhr.onerror(new Event('error'));
        return;
      }
      originalSend.call(xhr, body);
      xhr.addEventListener('loadend', function () { guardCartPage(); guardDynamicCheckoutButtons(); });
    });
  };

  // ---------------------------------------------------------------------
  // INIT
  // ---------------------------------------------------------------------
  function init() {
    guardCartPage();
    guardDynamicCheckoutButtons();

    // Lightweight poll as a fallback for cart-drawer apps/themes that update
    // the cart via mechanisms this script can't see (e.g. some section-
    // rendering APIs). One small fetch every few seconds is cheap.
    setInterval(function () {
      guardCartPage();
      guardDynamicCheckoutButtons();
    }, 3000);

    // Re-check when the browser restores a cached page (back/forward nav).
    window.addEventListener('pageshow', function () {
      guardCartPage();
      guardDynamicCheckoutButtons();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API in case your theme/cart-drawer wants to trigger a re-check
  // manually right after it renders new cart markup.
  window.PassOnlyRestriction = {
    refresh: function () { guardCartPage(); guardDynamicCheckoutButtons(); },
    getCart: getCart,
    cartState: cartState
  };
})();